import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Workshop.css';

const TOOL_GROUPS = [
  {
    id: 'power',
    label: 'POWER TOOLS · WOODWORKING',
    icon: '🪚',
    tools: [
      { id: 'drill-press', name: 'King Canada 8" Bench Drill Press', model: 'KC-108N' },
      { id: 'miter-saw', name: 'DEWALT Sliding Compound Miter Saw 12"', model: 'DWS779' },
      { id: 'band-saw', name: 'VEVOR 20V Portable Band Saw', model: '5" deep · brushless' },
      { id: 'scroll-saw', name: 'Dremel Moto-Saw', model: 'MS20-01 · variable speed' },
      { id: 'belt-disc-sander', name: 'Woodskil Belt & Disc Sander', model: '4×36" belt + 6" disc · 3/4HP' },
      { id: 'recip-saw', name: 'DEWALT 20V XR Reciprocating Saw', model: 'DCS367B' },
      { id: 'drill-combo', name: 'DEWALT 20V Cordless Drill Combo (5-tool)', model: 'DCK551D1M1' },
      { id: 'heat-gun', name: 'Heat Gun', model: '' },
      { id: 'workbench', name: 'Keter Heavy-Duty Folding Workbench', model: '1000 lb · 12" clamps' },
    ],
  },
  {
    id: 'laser',
    label: 'LASER · CRAFT (xTOOL)',
    icon: '🔥',
    tools: [
      { id: 'xtool-s1-20w', name: 'xTool S1 Enclosed Diode Laser Cutter', model: '20W' },
      { id: 'xtool-s1-ir', name: 'xTool S1 1064nm Infrared Laser Module', model: 'metal engraving' },
      { id: 'xtool-m1-ultra', name: 'xTool M1 Ultra (4-in-1) Ultimate Bundle', model: '10W laser · inkjet · blades · pens · RA2 Pro · Smart Press 12×10 · Mini Press' },
    ],
  },
  {
    id: '3dprint',
    label: '3D PRINTING · SCANNING',
    icon: '🧊',
    tools: [
      { id: 'bambu-x1c', name: 'Bambu Lab X1C', model: 'FDM · multi-material' },
      { id: 'bambu-p1s', name: 'Bambu Lab P1S', model: 'FDM · enclosed' },
      { id: 'photon-mono-m5', name: 'Anycubic Photon Mono M5', model: 'resin · 12K · 7.87×8.58×4.84"' },
      { id: 'wash-cure', name: 'Anycubic Wash & Cure 3.0', model: '165×100×180mm · 2-in-1' },
      { id: 'cr-scan-otter', name: 'Creality CR-Scan Otter 3D Scanner', model: 'combo + manual turntable (Otter/Raptor/Ferret)' },
    ],
  },
  {
    id: 'printers',
    label: 'PRINTERS · LABELS',
    icon: '🏷️',
    tools: [
      { id: 'canon-ivy2', name: 'Canon Ivy 2 Mini Photo Printer', model: 'sticky-back · iOS/Android' },
      { id: 'jadens-thermal', name: 'JADENS Bluetooth Thermal Label Printer', model: '4×6 shipping' },
      { id: 'polono-thermal', name: 'POLONO Bluetooth Thermal Label Printer', model: '4×6 shipping' },
    ],
  },
  {
    id: 'electronics',
    label: 'ELECTRONICS BENCH',
    icon: '⚡',
    tools: [
      { id: 'oscilloscope', name: 'Oscilloscope', model: '' },
      { id: 'multimeter', name: 'Multimeters', model: '' },
      { id: 'dc-supply', name: 'DC Bench Power Supply', model: '0–30V / 0–10A · 4-digit display' },
      { id: 'sig-gen', name: 'Digital Voltage / Current Generator', model: '0–10V · 0–22mA · USB · PLC simulator' },
      { id: 'soldering-kit', name: '60W Soldering Iron Kit', model: '5 tips · sucker · stand · tweezers · wire · cutter' },
      { id: 'microscope', name: 'Andonstar AD246S-M HDMI Microscope', model: '2000× · 7" LCD · 2160P · 3 lens' },
      { id: 'crimp-kit', name: 'Preciva Crimping Tool Kit', model: '1050pc · JST/Dupont/Molex/ATX · AWG28-18' },
    ],
  },
  {
    id: 'rf',
    label: 'RF · SIGNAL ANALYSIS',
    icon: '📡',
    tools: [
      { id: 'nanovna', name: 'AURSINC NanoVNA-H Vector Network Analyzer', model: '10kHz–1.5GHz · HW V3.6 · S-params · SWR · Smith chart' },
      { id: 'tinysa', name: 'AURSINC Tiny-SA Spectrum Analyzer', model: '100kHz–960MHz · V0.3.1 · 2.8" touch · sig gen · ESD' },
      { id: 'logic-analyzer', name: 'DKARDU USB Logic Analyzer', model: '24MHz · 8 channel · UART/I²C/SPI · hook clips' },
    ],
  },
  {
    id: 'studio',
    label: 'STUDIO · VIDEO',
    icon: '🎬',
    tools: [
      { id: 'dji-mini-4-pro', name: 'DJI Mini 4 Pro Drone', model: '4K HDR · obstacle sensing · ActiveTrack 360°' },
      { id: 'camera-kit', name: 'Camera + Tripod Kit', model: 'mirrorless body · lenses · tripods' },
      { id: 'lighting-kit', name: 'Studio Lighting Kit', model: 'softboxes · key/fill/rim · adjustable temp' },
      { id: 'greenscreen', name: 'Greenscreen Backdrop', model: 'chroma-key fabric + stand' },
    ],
  },
];

const ALL_TOOLS = TOOL_GROUPS.flatMap(g =>
  g.tools.map(t => ({
    ...t,
    groupId: g.id,
    groupLabel: g.label,
    groupIcon: g.icon,
    image: t.image || `/workshop/${t.id}.jpg`,
  }))
);

const encode = (data) =>
  Object.keys(data)
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(data[k]))
    .join('&');

const Workshop = () => {
  const navigate = useNavigate();
  const [now] = useState(new Date());
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [timeSlot, setTimeSlot] = useState('afternoon');
  const [selectedTools, setSelectedTools] = useState([]);
  const [project, setProject] = useState('');
  const [partsAck, setPartsAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [zoomTool, setZoomTool] = useState(null);
  const [imgErrors, setImgErrors] = useState({});

  const toggleTool = (id) => {
    setSelectedTools((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const onImgError = (id) => setImgErrors((e) => ({ ...e, [id]: true }));

  useEffect(() => {
    if (!zoomTool) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setZoomTool(null);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [zoomTool]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || selectedTools.length === 0) {
      setError("Just need your name and at least one tool you're interested in 🙂");
      return;
    }
    if (!partsAck) {
      setError("One quick check — tick the box below to confirm");
      return;
    }
    setSubmitting(true);
    setError(null);

    const toolNames = selectedTools
      .map((id) => {
        const t = ALL_TOOLS.find((x) => x.id === id);
        return t ? `${t.name}${t.model ? ` (${t.model})` : ''}` : id;
      })
      .join('\n');

    const payload = {
      'form-name': 'workshop-booking',
      name,
      date,
      timeSlot,
      tools: toolNames,
      project,
      partsAcknowledged: partsAck ? 'yes' : 'no',
    };

    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSubmitted(true);
    } catch (err) {
      setError("Couldn't send that — check your connection and try again");
    } finally {
      setSubmitting(false);
    }
  };

  const todayStr = now.toISOString().slice(0, 10);

  return (
    <div className="ws-root">
      <div className="ws-topbar">
        <button className="ws-back" onClick={() => navigate('/')}>◀ HOME</button>
        <span>KOMOK//WORKSHOP</span>
      </div>

      <div className="ws-header">
        <div className="ws-title">KOMOK WORKSHOP</div>
        <div className="ws-intro">
          Got some tools, for parts/consumables/etc, please contribute when can!
        </div>
      </div>

      <section className="ws-section">
        <div className="ws-section-label">▸ Pick your tools</div>
        <div className="ws-section-hint">Tap any that look fun. You can pick as many as you like.</div>
        {TOOL_GROUPS.map((group) => (
          <div className="ws-group" key={group.id}>
            <div className="ws-group-header">
              <span className="ws-group-icon">{group.icon}</span>
              <span className="ws-group-label">{group.label}</span>
            </div>
            <div className="ws-tools">
              {group.tools.map((t) => {
                const checked = selectedTools.includes(t.id);
                const fullTool = ALL_TOOLS.find((x) => x.id === t.id);
                const hasImg = !imgErrors[t.id];
                return (
                  <div
                    key={t.id}
                    className={`ws-tool ${checked ? 'selected' : ''}`}
                  >
                    <button
                      type="button"
                      className="ws-tool-thumb"
                      onClick={(e) => {
                        e.stopPropagation();
                        setZoomTool(fullTool);
                      }}
                      aria-label={`View image of ${t.name}`}
                    >
                      {hasImg ? (
                        <img
                          src={fullTool.image}
                          alt={t.name}
                          loading="lazy"
                          onError={() => onImgError(t.id)}
                        />
                      ) : (
                        <span className="ws-tool-thumb-fallback" aria-hidden="true">
                          {group.icon}
                        </span>
                      )}
                      <span className="ws-tool-thumb-zoom">⌕</span>
                    </button>
                    <button
                      type="button"
                      className="ws-tool-row"
                      onClick={() => toggleTool(t.id)}
                    >
                      <span className="ws-tool-meta">
                        <span className="ws-tool-name">{t.name}</span>
                        {t.model && <span className="ws-tool-model">{t.model}</span>}
                      </span>
                      <span className="ws-tool-check">{checked ? '✓' : '+'}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {zoomTool && (
        <div className="ws-zoom" onClick={() => setZoomTool(null)}>
          <div className="ws-zoom-inner" onClick={(e) => e.stopPropagation()}>
            <button className="ws-zoom-close" onClick={() => setZoomTool(null)} aria-label="Close">
              ✕
            </button>
            <div className="ws-zoom-image-wrap">
              {imgErrors[zoomTool.id] ? (
                <div className="ws-zoom-fallback">
                  <span className="ws-zoom-fallback-icon">{zoomTool.groupIcon}</span>
                  <span className="ws-zoom-fallback-msg">No image yet</span>
                </div>
              ) : (
                <img
                  src={zoomTool.image}
                  alt={zoomTool.name}
                  onError={() => onImgError(zoomTool.id)}
                />
              )}
            </div>
            <div className="ws-zoom-meta">
              <div className="ws-zoom-name">{zoomTool.name}</div>
              {zoomTool.model && <div className="ws-zoom-model">{zoomTool.model}</div>}
              <div className="ws-zoom-group">{zoomTool.groupIcon} {zoomTool.groupLabel}</div>
            </div>
            <button
              type="button"
              className={`ws-zoom-toggle ${selectedTools.includes(zoomTool.id) ? 'selected' : ''}`}
              onClick={() => toggleTool(zoomTool.id)}
            >
              {selectedTools.includes(zoomTool.id) ? '✓ Picked — tap to remove' : '+ Add to my picks'}
            </button>
          </div>
        </div>
      )}

      <section className="ws-section">
        <div className="ws-section-label">▸ When works for you?</div>
        {submitted ? (
          <div className="ws-success">
            <div className="ws-success-title">Got it, {name.split(' ')[0] || 'friend'}! 🛠️</div>
            <div className="ws-success-body">
              I'll be in touch soon to confirm. You're set for{' '}
              <strong>{selectedTools.length} tool{selectedTools.length === 1 ? '' : 's'}</strong>
              {date && <> on <strong>{date}</strong></>}.
            </div>
            <button
              type="button"
              className="ws-btn"
              onClick={() => {
                setSubmitted(false);
                setSelectedTools([]);
                setProject('');
              }}
            >
              + Submit another
            </button>
          </div>
        ) : (
          <form
            className="ws-form"
            name="workshop-booking"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
          >
            <input type="hidden" name="form-name" value="workshop-booking" />
            <p className="ws-honeypot" aria-hidden="true">
              <label>Don't fill this: <input name="bot-field" tabIndex="-1" autoComplete="off" /></label>
            </p>

            <label className="ws-field">
              <span className="ws-field-label">Your name</span>
              <input
                type="text"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                placeholder="What should I call you?"
                required
              />
            </label>

            <div className="ws-row ws-row-2">
              <label className="ws-field">
                <span className="ws-field-label">Day (rough is fine)</span>
                <input
                  type="date"
                  name="date"
                  min={todayStr}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </label>
              <label className="ws-field">
                <span className="ws-field-label">Time of day</span>
                <select
                  name="timeSlot"
                  value={timeSlot}
                  onChange={(e) => setTimeSlot(e.target.value)}
                >
                  <option value="morning">Morning</option>
                  <option value="afternoon">Afternoon</option>
                  <option value="evening">Evening</option>
                  <option value="all-day">Whenever works</option>
                </select>
              </label>
            </div>

            <div className="ws-selection-summary">
              <span className="ws-field-label">Tools you picked</span>
              <span className="ws-selection-count">
                {selectedTools.length === 0 ? (
                  <em>None yet — scroll up and tap a few</em>
                ) : (
                  `${selectedTools.length} ${selectedTools.length === 1 ? 'tool' : 'tools'} selected`
                )}
              </span>
            </div>

            <label className="ws-field">
              <span className="ws-field-label">What are you building? (optional)</span>
              <textarea
                name="project"
                rows={3}
                value={project}
                onChange={(e) => setProject(e.target.value)}
                placeholder="A drone, a sign, a robot, a custom keyboard, who knows..."
              />
            </label>

            <label className="ws-checkbox">
              <input
                type="checkbox"
                name="partsAcknowledged"
                checked={partsAck}
                onChange={(e) => setPartsAck(e.target.checked)}
                required
              />
              <span>
                Sounds good — I'll chip in if I burn through parts.
              </span>
            </label>

            {error && <div className="ws-error">{error}</div>}

            <button type="submit" className="ws-btn ws-btn-submit" disabled={submitting}>
              {submitting ? 'Sending...' : "Let's build something →"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
};

export default Workshop;
